import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Clock, Mail, MapPin, Grid, List } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { ScheduledLetterActions } from "@/components/letters/ScheduledLetterActions";
import { DeleteLetterDialog } from "@/components/letters/DeleteLetterDialog";

export default function ScheduledLetters() {
  const { isAdmin, userPlan, isLoading: isAdminLoading } = useUserProfile();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [letterToDelete, setLetterToDelete] = useState<string | null>(null);

  const { data: profileRole, isLoading: isRoleLoading } = useQuery({
    queryKey: ["scheduled-letters-profile-role"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", session.user.id)
        .maybeSingle();
      if (error || !profile?.role) return null;
      return profile.role;
    },
    enabled: !isAdminLoading,
  });
  const adminFromProfile = profileRole === "admin";

  const { data: letters, isLoading: isLettersLoading } = useQuery({
    queryKey: ["scheduled-letters", adminFromProfile],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return [];
      
      const today = new Date().toISOString().split('T')[0];
      
      let query = supabase
        .from("letters_with_owner")
        .select("*")
        .gt("delivery_date", today) // Lógica corrigida: usa a data
        .eq("payment_status", "paid")
        .order("delivery_date", { ascending: true });

      if (!adminFromProfile) {
        query = query.eq("user_id", session.user.id);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !isAdminLoading && !isRoleLoading,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("letters").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Carta excluída com sucesso!" });
      queryClient.invalidateQueries({ queryKey: ["scheduled-letters"] });
      setLetterToDelete(null);
    },
    onError: () => {
      toast({ variant: "destructive", title: "Erro ao excluir a carta." });
    },
  });

  const handleEdit = (id: string) => navigate(`/admin/editar-carta/${id}`);
  const handleDeleteConfirm = () => {
    if (letterToDelete) {
      deleteMutation.mutate(letterToDelete);
    }
  };

  const isLoading = isAdminLoading || isRoleLoading || isLettersLoading;

  const visibleOwnerEmail = (email: string | null | undefined) =>
    adminFromProfile && email ? email : null;

  if (isLoading) {
    return <div>Carregando...</div>;
  }

  const renderCards = () => (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {letters!.map((letter) => (
        <Card key={letter.id} className="hover:shadow-lg transition-shadow duration-200 flex flex-col">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5 text-amber-600" />
                Carta Agendada
              </CardTitle>
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                <Clock className="h-3 w-3 mr-1" />
                Agendada
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 flex-grow flex flex-col">
            <div className="flex-grow space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4" />
                <span>
                  Entrega: {format(new Date(letter.delivery_date + "T00:00:00"), "dd 'de' MMMM, yyyy", { locale: ptBR })}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin className="h-4 w-4" />
                <span className="capitalize">{letter.plan_type || "Padrão"}</span>
              </div>
              {visibleOwnerEmail(letter.owner_email) && (
                <div className="text-sm text-gray-600">
                  <strong>Email:</strong> {visibleOwnerEmail(letter.owner_email)}
                </div>
              )}
            </div>
            <div className="pt-2">
              <ScheduledLetterActions
                letterId={letter.id!}
                userPlan={userPlan}
                isAdmin={isAdmin}
                onEdit={handleEdit}
                onDelete={() => setLetterToDelete(letter.id)}
              />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderTable = () => (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="text-left p-4 font-semibold">Data de Entrega</th>
              <th className="text-left p-4 font-semibold">Plano</th>
              {adminFromProfile && <th className="text-left p-4 font-semibold">Email</th>}
              <th className="text-right p-4 font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody>
            {letters!.map((letter) => (
              <tr key={letter.id} className="border-b hover:bg-gray-50">
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-amber-500" />
                    {format(new Date(letter.delivery_date + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR })}
                  </div>
                </td>
                <td className="p-4 capitalize">{letter.plan_type || "Padrão"}</td>
                {adminFromProfile && <td className="p-4">{visibleOwnerEmail(letter.owner_email) ?? ""}</td>}
                <td className="p-4">
                  <ScheduledLetterActions
                    letterId={letter.id!}
                    userPlan={userPlan}
                    isAdmin={isAdmin}
                    onEdit={handleEdit}
                    onDelete={() => setLetterToDelete(letter.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Cartas Agendadas</h1>
          <p className="text-gray-600 mt-2">Acompanhe e gerencie suas cartas programadas</p>
        </div>
        {letters && letters.length > 0 && (
          <div className="flex gap-2">
            <Button variant={viewMode === 'cards' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('cards')}>
              <Grid className="h-4 w-4" />
            </Button>
            <Button variant={viewMode === 'table' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('table')}>
              <List className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {letters && letters.length > 0 ? (
        viewMode === 'cards' ? renderCards() : renderTable()
      ) : (
        <Card className="text-center py-12">
          <CardContent>
            <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma carta agendada</h3>
            <p className="text-gray-600 mb-6">Você não tem cartas programadas para envio no momento.</p>
            <Button onClick={() => navigate('/admin/escrever-carta')}>
              Escrever Nova Carta
            </Button>
          </CardContent>
        </Card>
      )}
      <DeleteLetterDialog
        isOpen={!!letterToDelete}
        onClose={() => setLetterToDelete(null)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}